import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Tooltip,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Popover,
  CssBaseline,
  Menu,
  MenuItem,
  Switch,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";
import apiService from "../services/apiService";

function Client_details() {
  const [formData, setFormData] = useState({
    cl_id: "",
    name: "",
    active_flag: 1,
  });
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [visibleColumns, setVisibleColumns] = useState({
    cl_id: true,
    name: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [data, setData] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteClientId, setDeleteClientId] = useState(null);
  const [originalFormData, setOriginalFormData] = useState({});

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false); // Add loading state
  const [filters, setFilters] = useState({ cl_id: "", name: "" });
  const [tempFilters, setTempFilters] = useState({ cl_id: "", name: "" });
  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const columnLabels = {
    cl_id: "Client ID",
    name: "Client",
  };
  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Check if form data has changed
  const hasFormChanged = () => {
    if (!isEditing) return true; // Always allow submit for new entries
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required.";
    if (!formData.cl_id) errors.cl_id = "Client ID is required.";
    return errors;
  };

  const fetchData = useCallback(async () => {
    setLoading(true); // Start loading
    const params = new URLSearchParams({ page, limit: rowsPerPage });

    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim() !== "") {
        params.append(key, value);
      }
    });
    try {
      const result = await apiService.get(
        `/clients/paginate/list?${params.toString()}`,
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false); // End loading
    }
  }, [BASE_URL, page, rowsPerPage, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const method = isEditing ? "PUT" : "POST";
    const endpoint = isEditing ? `/clients/${formData.cl_id}` : "/clients";

    try {
      if (isEditing) {
        await apiService.put(endpoint, formData);
      } else {
        await apiService.post(endpoint, formData);
      }

      setSuccessMessage(
        isEditing ? "User updated successfully!" : "Successfully Registered!",
      );
      setFormData({ cl_id: "", name: "", active_flag: 1 });
      setIsEditing(false);
      fetchData();
      setOpenDialog(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving client:", error);
      setSuccessMessage("");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleEdit = (user) => {
    setFormData(user);
    setOriginalFormData(user); // Store original data for comparison
    setIsEditing(true);
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleDelete = (cl_id) => {
    setDeleteClientId(cl_id);
    setOpenDeleteDialog(true);
  };

  const handleDialogOpen = () => {
    setFormData({ cl_id: "", name: "" });
    setFormErrors({});
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const columns = ["cl_id", "name"];

  const filteredUsers = data
    .filter((app) =>
      Object.entries(filters).every(([key, value]) =>
        app[key].toString().toLowerCase().includes(value.toLowerCase()),
      ),
    )
    .filter((app) =>
      searchTerm.trim() === ""
        ? true
        : Object.values(app)
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
    );
  const handleToggleFlag = async (cl_id, active_flag) => {
    try {
      const newFlag = active_flag === 1 ? 0 : 1;

      await apiService.put(`/clients/${cl_id}`, { active_flag: newFlag });

      fetchData();
    } catch (error) {
      console.error("Error toggling active flag:", error);
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Clients Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Clients
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + - DISABLED */}
            <Button
              onClick={handleDialogOpen}
              disabled={true}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                  cursor: "not-allowed",
                },
              }}
            >
              Add +
            </Button>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={handleOpen}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "Bold",
                }}
              >
                {columnLabels[col] || col}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col] || col}
                            <IconButton
                              onClick={(e) => handleFilterIconClick(e, col)}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempFilters[col]}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={4} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <TableRow key={user.cl_id}>
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(
                                  user.cl_id,
                                  parseInt(user.active_flag),
                                )
                              }
                              title="Toggle Active Flag"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    parseInt(user.active_flag) === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(user)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(user.cl_id)}
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {visibleColumns.cl_id && (
                          <TableCell>{user.cl_id}</TableCell>
                        )}
                        {visibleColumns.name && (
                          <TableCell>{user.name}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={4} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No matching data found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>
      </Box>

      {/* Add/Edit Dialog */}
      <ThemeProvider theme={theme}>
        <Dialog
          open={openDialog}
          // onClose={handleDialogClose}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDialog(false);
          }}
          width="small"
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              fontSize: "16px",
              py: 1,
            }}
          >
            {isEditing ? "Edit Client" : "Add Client"}
            <IconButton
              aria-label="close"
              onClick={handleDialogClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1.1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                label="Client ID"
                name="cl_id"
                value={formData.cl_id}
                onChange={handleChange}
                required
                margin="normal"
                error={!!formErrors.cl_id}
                helperText={formErrors.cl_id}
                sx={{ minWidth: 140 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                margin="normal"
                error={!!formErrors.name}
                helperText={formErrors.name}
                sx={{ minWidth: 140 }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={isEditing && !hasFormChanged()}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDeleteDialog(false);
            setDeleteClientId(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold", fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}>
            Confirm Delete
          </DialogTitle>

          <DialogContent>
            <Typography fontSize="0.9rem">
              {/* Are you sure you want to delete Client ID{" "}
              <strong>{deleteClientId}</strong> ? */}
              Are you sure you want to delete Client{" "}
              <strong>
                {data.find((client) => client.cl_id === deleteClientId)?.name}
              </strong>{" "}
              ?
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeleteClientId(null);
              }}
              sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await apiService.delete(`/clients/${deleteClientId}`);
                  setSuccessMessage("Client deleted successfully!");
                  fetchData();
                  setTimeout(() => setSuccessMessage(""), 3000);
                } catch (error) {
                  console.error("Error deleting client:", error);
                } finally {
                  setOpenDeleteDialog(false);
                  setDeleteClientId(null);
                }
              }}
              sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </Container>
  );
}

export default Client_details;
